import { useState, useEffect, useRef } from 'react';
import api from '@/lib/api';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';
import { Plus, Edit, Heart, RefreshCw, XCircle, Upload, Loader2, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const defaultForm = { title: '', description: '', target_amount: 0, deadline: '', thumbnail: '', is_anonymous_allowed: true, preset_amounts: [10000, 25000, 50000, 100000], status: 'active' };

export default function TenantDonations() {
  const [campaigns, setCampaigns] = useState([]);
  const [recurringDonations, setRecurringDonations] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaultForm);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetch = async () => { const r = await api.get('/tenant/donations/campaigns'); setCampaigns(r.data.campaigns); };
  const fetchRecurring = async () => { try { const r = await api.get('/tenant/donations/recurring'); setRecurringDonations(r.data.recurring_donations || []); } catch { } };
  useEffect(() => { fetch(); fetchRecurring(); }, []);

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast.error('Judul campaign wajib diisi');
      return;
    }
    try {
      if (editing) await api.put(`/tenant/donations/campaigns/${editing}`, form);
      else await api.post('/tenant/donations/campaigns', form);
      toast.success(editing ? 'Campaign diperbarui' : 'Campaign dibuat');
      setShowDialog(false); setEditing(null); setForm(defaultForm); fetch();
    } catch {
      toast.error('Gagal menyimpan campaign');
    }
  };

  const edit = (c) => { setEditing(c.id); setForm(c); setShowDialog(true); };

  const handleUploadThumbnail = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Pilih file gambar (JPG, PNG, WEBP)');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Ukuran gambar maksimal 5MB');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/tenant/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((prev) => ({ ...prev, thumbnail: res.data.url }));
      toast.success('Gambar berhasil diupload');
    } catch {
      toast.error('Gagal upload gambar');
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const cancelRecurring = async (id) => {
    if (!window.confirm('Batalkan donasi recurring ini?')) return;
    await api.put(`/tenant/donations/recurring/${id}/cancel`);
    fetchRecurring();
  };
  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);

  return (
    <div data-testid="tenant-donations-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>Donasi</h1>
        <Button onClick={() => { setEditing(null); setForm(defaultForm); setShowDialog(true); }} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="add-campaign-btn"><Plus size={16} className="mr-2" /> Buat Campaign</Button>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {campaigns.map(c => {
          const pct = c.target_amount > 0 ? Math.min(100, (c.total_raised / c.target_amount) * 100) : 0;
          return (
            <Card key={c.id} className="border-slate-200 shadow-sm overflow-hidden">
              <div className="h-36 bg-gradient-to-br from-pink-50 to-rose-100 flex items-center justify-center overflow-hidden">
                {c.thumbnail ? (
                  <img src={c.thumbnail} alt={c.title} className="w-full h-full object-cover" />
                ) : (
                  <Heart size={40} className="text-pink-300" />
                )}
              </div>
              <CardContent className="p-4">
                <div className="flex justify-between items-start">
                  <h3 className="font-semibold text-slate-900">{c.title}</h3>
                  <Button variant="ghost" size="icon" onClick={() => edit(c)}><Edit size={14} /></Button>
                </div>
                <p className="text-sm text-slate-600 mt-1 line-clamp-2">{c.description}</p>
                <div className="mt-4">
                  <div className="flex justify-between text-sm mb-1">
                    <span className="font-bold text-[#2D6CDF]">Rp {fmt(c.total_raised)}</span>
                    <span className="text-slate-500">/ Rp {fmt(c.target_amount)}</span>
                  </div>
                  <Progress value={pct} className="h-2" />
                </div>
                <div className="flex justify-between mt-3 text-xs text-slate-500">
                  <span>{c.donor_count} donatur</span>
                  <span className={`px-2 py-0.5 rounded-full font-medium ${c.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>{c.status}</span>
                </div>
              </CardContent>
            </Card>
          );
        })}
        {!campaigns.length && <div className="col-span-full py-12 text-center text-slate-400">Belum ada campaign donasi</div>}
      </div>

      {/* Recurring Donations Section */}
      {recurringDonations.length > 0 && (
        <div className="mt-8">
          <h2 className="text-lg font-semibold text-slate-900 mb-4 flex items-center gap-2">
            <RefreshCw size={18} className="text-pink-500" /> Donasi Recurring ({recurringDonations.length})
          </h2>
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-slate-100">
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Donatur</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Nominal</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Frekuensi</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Charge Berikutnya</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Total Donasi</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                  </tr>
                </thead>
                <tbody>
                  {recurringDonations.map(rd => (
                    <tr key={rd.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                      <td className="py-3 px-4">
                        <p className="font-medium">{rd.is_anonymous ? 'Anonymous' : rd.donor_name}</p>
                        <p className="text-xs text-slate-500">{rd.donor_email}</p>
                      </td>
                      <td className="py-3 px-4 font-medium">Rp {fmt(rd.amount)}</td>
                      <td className="py-3 px-4">
                        <Badge variant="outline" className="bg-pink-50 text-pink-700">
                          {rd.frequency === 'weekly' ? 'Mingguan' : rd.frequency === 'monthly' ? 'Bulanan' : 'Tahunan'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm">{rd.next_charge_date ? new Date(rd.next_charge_date).toLocaleDateString('id-ID') : '-'}</td>
                      <td className="py-3 px-4">
                        <p className="font-medium">Rp {fmt(rd.total_donated)}</p>
                        <p className="text-xs text-slate-500">{rd.donation_count}x donasi</p>
                      </td>
                      <td className="py-3 px-4">
                        <Badge className={rd.status === 'active' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-500'}>
                          {rd.status === 'active' ? 'Aktif' : 'Dibatalkan'}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-right">
                        {rd.status === 'active' && (
                          <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-700" onClick={() => cancelRecurring(rd.id)}>
                            <XCircle size={14} className="mr-1" /> Batalkan
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        </div>
      )}

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? 'Edit Campaign' : 'Buat Campaign'}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Judul</Label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="campaign-title-input" />
            </div>
            <div>
              <Label>Deskripsi</Label>
              <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
            </div>
            <div className="space-y-2">
              <Label>Gambar Campaign</Label>
              {form.thumbnail ? (
                <div className="relative rounded-lg overflow-hidden border border-slate-200">
                  <img src={form.thumbnail} alt="Preview campaign" className="w-full h-40 object-cover" />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => setForm({ ...form, thumbnail: '' })}
                  >
                    Hapus
                  </Button>
                </div>
              ) : (
                <div className="h-32 rounded-lg border-2 border-dashed border-slate-200 bg-slate-50 flex flex-col items-center justify-center text-slate-400">
                  <ImageIcon size={28} className="mb-2" />
                  <p className="text-xs">Belum ada gambar</p>
                </div>
              )}
              <div className="flex gap-2">
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp,image/gif"
                  className="hidden"
                  onChange={handleUploadThumbnail}
                />
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  disabled={uploading}
                  onClick={() => fileInputRef.current?.click()}
                  data-testid="campaign-upload-btn"
                >
                  {uploading ? <Loader2 size={16} className="mr-2 animate-spin" /> : <Upload size={16} className="mr-2" />}
                  {uploading ? 'Mengupload...' : 'Upload Gambar'}
                </Button>
              </div>
              <div>
                <Label className="text-xs text-slate-500">Atau tempel URL gambar</Label>
                <Input
                  value={form.thumbnail}
                  onChange={(e) => setForm({ ...form, thumbnail: e.target.value })}
                  placeholder="https://..."
                  className="mt-1"
                  data-testid="campaign-thumbnail-input"
                />
              </div>
              <p className="text-xs text-slate-400">Format JPG, PNG, WEBP. Maks 5MB.</p>
            </div>
            <div>
              <Label>Target Dana (Rp)</Label>
              <Input type="number" value={form.target_amount} onChange={(e) => setForm({ ...form, target_amount: parseInt(e.target.value) || 0 })} />
            </div>
            <div>
              <Label>Deadline</Label>
              <Input type="date" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} />
            </div>
            <Button onClick={handleSave} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="save-campaign-btn">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
